import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { loadGoogleMaps } from './googleMapsLoader'
import { useGeocodedLocations } from './useGeocodedLocations'

const MAP_ID = 'DEMO_MAP_ID'

export default function InteractiveMap({ subject, comps, height = 400 }) {
  const containerRef = useRef(null)
  const mapRef = useRef(null) // google.maps.Map
  const markersRef = useRef([]) // AdvancedMarkerElement[]
  const streetViewMarkersRef = useRef([]) // Legacy markers for Street View compatibility
  const infoWindowRef = useRef(null) // google.maps.InfoWindow
  const addresses = [subject, ...comps.map(c => c.fullAddressCityStateZip)].filter(Boolean)
  const { loading: geoLoading, locations } = useGeocodedLocations(addresses)

  // 1.  Load Maps JS API once.
  useEffect(() => { loadGoogleMaps() }, [])

  // 2.  When both API + geocodes ready → build / refresh map.
  useEffect(() => {
    if (geoLoading || !window.google?.maps?.Map || !locations.length) { return }

    // 2a. Create map and info window only once.
    if (!mapRef.current) {
      mapRef.current = new window.google.maps.Map(containerRef.current, {
        center: { lat: 39.5, lng: -98.35 }, // USA centroid until fitBounds()
        zoom: 4,
        mapId: MAP_ID, // TODO: Set this up in Google Cloud Console
        mapTypeId: 'roadmap',
      })
      infoWindowRef.current = new window.google.maps.InfoWindow()
    }


    // 2b. Clear previous markers.
    markersRef.current.forEach(m => { m.map = null })
    markersRef.current = []
    streetViewMarkersRef.current.forEach(m => { m.setMap(null) })
    streetViewMarkersRef.current = []

    // 2c. Add Subject (index 0 is subject).
    const [subjectLoc, ...compLocs] = locations
    if (subjectLoc) {
      addMarker(subjectLoc, 'S', '#EA4335', 'Subject', subject)
    }

    // 2d. Add comps.
    compLocs.forEach((loc, i) => {
      const comp = comps[i]
      addMarker(loc, `${i + 1}`, '#4285F4', `Comparable ${comp.number}`, comp.fullAddressCityStateZip)
    })

    // 2e. Fit bounds.
    if (locations.length > 0) {
      const bounds = new window.google.maps.LatLngBounds()
      locations.forEach(bounds.extend, bounds)
      mapRef.current.fitBounds(bounds)
      if (locations.length === 1) {
        mapRef.current.setZoom(15)
      }
    }


    // helper
    function addMarker(position, glyph, color, label, address) {
      // Create Advanced Marker for map view
      const pin = new window.google.maps.marker.PinElement({
        glyph,
        glyphColor: 'white',
        background: color,
        borderColor: 'grey',
      })
      const advancedMarker = new window.google.maps.marker.AdvancedMarkerElement({
        map: mapRef.current,
        position,
        content: pin.element,
        title: `${label}: ${address}`, // for hover
      })

      advancedMarker.addListener('click', () => {
        infoWindowRef.current.close()
        infoWindowRef.current.setContent(`<strong>${label}</strong><br/>${address}`)
        infoWindowRef.current.open(mapRef.current, advancedMarker)
      })

      // Create legacy marker for Street View compatibility (hidden by default)
      // https://issuetracker.google.com/issues/282790637?pli=1
      const legacyMarker = new window.google.maps.Marker({
        position,
        map: mapRef.current,
        title: `${label}: ${address}`,
        visible: false, // Hidden by default - will be shown in Street View
        icon: {
          // Create a custom icon that matches the Advanced Marker appearance
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.9,
          strokeColor: 'white',
          strokeWeight: 3,
          scale: 36,
          labelOrigin: new window.google.maps.Point(0, 0),
        },
        label: {
          text: glyph,
          color: 'white',
          fontSize: '16px', // Increased from 12px to 16px
          fontWeight: 'bold',
        },
      })

      legacyMarker.addListener('click', () => {
        infoWindowRef.current.close()
        infoWindowRef.current.setContent(`<strong>${label}</strong><br/>${address}`)
        infoWindowRef.current.open(mapRef.current, legacyMarker)
      })

      markersRef.current.push(advancedMarker)
      streetViewMarkersRef.current.push(legacyMarker)
    }
  }, [geoLoading, locations, subject, comps])

  // 3. Handle Street View visibility of markers - runs after map and markers are created
  useEffect(() => {
    if (!mapRef.current || markersRef.current.length === 0 || streetViewMarkersRef.current.length === 0) { return }

    const handleStreetViewChanged = () => {
      const streetViewPanorama = mapRef.current.getStreetView()
      const isStreetViewVisible = streetViewPanorama.getVisible()

      // Toggle marker visibility based on Street View state
      markersRef.current.forEach(marker => {
        marker.map = isStreetViewVisible ? null : mapRef.current
      })

      streetViewMarkersRef.current.forEach(marker => {
        marker.setVisible(isStreetViewVisible)
      })
    }

    // Listen for Street View visibility changes
    const streetViewPanorama = mapRef.current.getStreetView()
    const listener = streetViewPanorama.addListener('visible_changed', handleStreetViewChanged)

    return () => {
      if (listener) {
        window.google.maps.event.removeListener(listener)
      }
    }
  }, [geoLoading, locations]) // Re-run when markers are recreated

  return (
    <div style={{ width: '100%', height }} ref={containerRef}>
      {geoLoading && <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading map...</div>}
    </div>
  )
}

InteractiveMap.propTypes = {
  subject: PropTypes.string,
  comps: PropTypes.arrayOf(
    PropTypes.shape({
      fullAddressCityStateZip: PropTypes.string.isRequired,
      number: PropTypes.number.isRequired,
    })
  ),
  height: PropTypes.number,
}

InteractiveMap.defaultProps = {
  subject: '',
  comps: [],
  height: 400,
}
